import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'How to Raise Support Fast: A 30/60/90-Day Plan for New Missionaries';
const DESCRIPTION =
    'A realistic 30/60/90-day plan for raising full missionary support quickly — what to build first, who to contact when, and the weekly rhythm that keeps momentum from stalling.';

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'HowTo',
            name: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover11.jpg',
            step: [
                {
                    '@type': 'HowToStep',
                    name: 'Days 1–30: Build your foundation',
                    text: 'Write your support letter, set up your giving page, and build your full contact list before making any asks.',
                },
                {
                    '@type': 'HowToStep',
                    name: 'Days 31–60: Make the asks',
                    text: 'Send letters in weekly batches, follow up by phone or text within a week, and track every response.',
                },
                {
                    '@type': 'HowToStep',
                    name: 'Days 61–90: Follow up and close gaps',
                    text: 'Re-contact non-responders, ask committed donors for referrals, and target your remaining gap with a second wave.',
                },
            ],
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/raise-support-fast-30-60-90-day-plan',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'Is it realistic to raise full missionary support in 90 days?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "For some missionaries with a large existing network, yes. For most, 90 days is enough to get well past halfway if you work the plan consistently — full support more commonly takes several months. Treat the 90-day plan as a way to build unstoppable momentum, not a hard deadline.",
                    },
                },
                {
                    '@type': 'Question',
                    name: "What should I do in the first 30 days if I haven't sent any letters yet?",
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Resist the urge to start asking immediately. Spend the first 30 days building your list, writing and refining your support letter, and setting up a giving page. Asking before these are ready wastes your best contacts on a weak first impression.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How many people should I contact per week?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Most missionaries sustain 15–25 personal contacts a week without burning out. Batch your list into weekly groups so you always know exactly who to follow up with next, rather than sending everything at once and losing track.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'What if I still have a support gap after 90 days?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'A remaining gap after 90 days is common and not a sign of failure. Ask your committed donors for two or three referrals each, revisit anyone who said "not now," and keep your page and letter working in the background — recurring support tends to keep arriving well after the initial push.',
                    },
                },
            ],
        },
    ],
};

export default function RaiseSupportFast306090DayPlan() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/raise-support-fast-30-60-90-day-plan' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-raise-support-fast-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover11.jpg" alt="A small wooden cross planted in soil beside new green sprouts, representing the early, careful work of building support from scratch" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Most missionaries under-plan the first 30 days and over-plan everything after. They fire off a few letters, make some calls, then run out of structure and momentum stalls somewhere around week six. A simple 30/60/90-day plan fixes that: each phase has one job, so you always know what to do next instead of guessing.</p>

                    <h2>Days 1–30: Build your foundation</h2>

                    <p>Do not ask anyone for money in the first 30 days. Use this phase to build the three things every later ask depends on: your full contact list (aim for 100–150 names, more than you think you'll need), a clear <a href="/resources/how-to-write-a-missionary-support-letter">support letter</a> that states your calling, your need, and one obvious next step, and a giving page that makes recurring gifts effortless. A weak letter or a clunky page will quietly cap your response rate for the entire campaign — get these right before you contact anyone.</p>

                    <p>Sort your list into three tiers as you build it: close relationships who already know your work, warm contacts who'd recognize your name, and cold contacts you'll need a stronger case for. This sorting takes an afternoon and saves weeks later, because it tells you exactly who to approach first.</p>

                    <h2>Days 31–60: Make the asks</h2>

                    <p>Start with your closest tier. Send 15–25 personalized letters or emails a week rather than blasting your whole list at once — a smaller, staggered batch means you can actually follow up on every single one, and early responses often reveal what's landing and what needs adjusting before you reach your wider circle. Follow up by phone or text within a week of each letter; a follow-up roughly doubles response rates compared to a letter sent cold with no contact after.</p>

                    <p>Track every contact in a simple spreadsheet: sent, followed up, response, outcome. It feels like overhead in week one and becomes essential by week five, when you're juggling responses from three different batches at once and can't rely on memory alone.</p>

                    <p>Move to your warm and cold tiers as your close tier is exhausted, using the same letter with small adjustments for context. By day 60 you should have contacted your entire list at least once.</p>

                    <h2>Days 61–90: Follow up and close gaps</h2>

                    <p>The final phase is where most of the remaining ground gets made up, not through new asks but through follow-up on the ones you've already sent. Re-contact anyone who hasn't responded — a second touch, not a guilt trip, phrased as a genuine check-in. Many "no responses" are simply people who meant to reply and didn't; a short, low-pressure follow-up recovers a meaningful share of them.</p>

                    <p>Ask every committed donor for two or three referrals: people in their circle who might also want to give. Referred contacts convert noticeably better than cold ones, because there's already a trusted relationship vouching for you. If you still have a gap at day 90, target it specifically — identify the dollar amount remaining and the number of donors at your average gift size needed to close it, rather than continuing to contact your whole list generically.</p>

                    <h2>Keep a weekly rhythm, not a daily scramble</h2>

                    <p>Set one weekly block for support-raising admin: reviewing your tracker, sending the next batch, scheduling follow-ups. A consistent weekly rhythm across all 90 days outperforms sporadic bursts of intense effort followed by weeks of nothing — donors respond to a campaign that feels steady and organized, and you'll avoid the burnout that comes from treating every week like a sprint.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>Is it realistic to raise full missionary support in 90 days?</strong><br />
                    For some, yes — for most, 90 days gets you well past halfway if you work the plan consistently. Treat it as momentum, not a hard deadline.</p>

                    <p><strong>What should I do in the first 30 days if I haven't sent any letters yet?</strong><br />
                    Build your list and refine your letter and page first. Asking before these are ready wastes your best contacts on a weak first impression.</p>

                    <p><strong>How many people should I contact per week?</strong><br />
                    Most missionaries sustain 15–25 personal contacts a week without burning out — batch your list so you always know who's next.</p>

                    <p><strong>What if I still have a support gap after 90 days?</strong><br />
                    Common, and not a failure. Ask committed donors for referrals, revisit "not now" responses, and let your page keep working in the background.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/how-to-write-a-missionary-support-letter">How to Write a Missionary Support Letter That Actually Gets You Funded</a></li>
                        <li><a href="/resources/how-long-to-raise-full-support-as-a-missionary">How Long Does It Take to Raise Full Support as a Missionary?</a></li>
                        <li><a href="/resources/how-much-support-does-a-missionary-need">How Much Support Does a Missionary Need? A Realistic Monthly Budget Breakdown</a></li>
                        <li><a href="/resources/missionary-support-raising-launch-checklist">Missionary Support-Raising Launch Checklist</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We built this plan from the pattern we kept seeing: missionaries who structured their first 90 days closed their gap faster than missionaries who worked just as hard without one.</p>
                    <p><small><em>Last updated: 2026-07-27.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/raise-support-fast-30-60-90-day-plan" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
